import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { experimentalStyled as styled, useTheme } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import ArrowBack from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import {
  fetchHouseholdSearchResults,
  clearHouseholdSearchResults,
} from 'src/redux/slices/Compliance/HouseholdSearch';
import StatusNotesForm from './StatusNotesForm';
import { SelectedMemberDetails } from './SelectedMemberDetails';
import { StatusNotesRecentList } from './StatusNotesRecentList';
import SearchNotFound from '../../../SearchNotFound';
import Scrollbar from '../../../Scrollbar';
import HouseholdLanguage from '../../../Household/HouseholdLanguage';
import useAuth from 'src/hooks/useAuth';
import {
  matchLabels,
  splitHighlights,
  tokenizeSearch,
} from 'src/utils/householdSearchHighlight';

const HouseholdMemberBoxStyle = styled(Box)(({ theme }) => ({
  borderLeft: `3px solid ${theme.palette.background.default}`,
  '&:hover': {
    backgroundColor: `${theme.palette.action.hover} !important`,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
}));

const HighlightedText = ({ text, query }) => {
  if (!text) {
    return null;
  }
  if (!query) {
    return <>{text}</>;
  }
  const tokens = tokenizeSearch(query);
  // Also try the whole query as a literal token so "Chaya HOTMA" highlights
  // the contiguous substring if it appears (e.g. in household.name).
  const allTokens = Array.from(
    new Set([query.trim(), ...tokens].filter(Boolean))
  );
  const segments = splitHighlights(text, allTokens);
  return (
    <>
      {segments.map((seg, idx) =>
        seg.matched ? (
          <Box
            key={idx}
            component="mark"
            sx={{
              bgcolor: 'warning.lighter',
              color: 'inherit',
              px: 0.25,
              borderRadius: 0.5,
            }}
          >
            {seg.text}
          </Box>
        ) : (
          <span key={idx}>{seg.text}</span>
        )
      )}
    </>
  );
};

const HouseholdMemberLabel = ({
  member,
  handleClick,
  certificationCategory,
  certification,
  searchQuery,
}) => {
  const theme = useTheme();
  const hints = matchLabels(member?.matched_fields, searchQuery);

  return (
    <HouseholdMemberBoxStyle
      style={{ cursor: 'pointer' }}
      px={2}
      py={1}
      onClick={() => handleClick(member, certification)}
      data-testid="top-bar-search-result"
    >
      <Stack
        direction="row"
        sx={{
          color: theme.palette.text.primary,
          gap: 0.5,
          whiteSpace: 'nowrap',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="body2"
          style={{ lineHeight: '10px', textTransform: 'capitalize' }}
          sx={{ mt: 1 }}
        >
          <HighlightedText
            text={`${member.first_name} ${member.last_name}`}
            query={searchQuery}
          />
          <HouseholdLanguage
            preferredLanguage={member?.preferred_language?.name}
          />
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {' '}
            {`(${member.member_type.display_name || member.member_type.name})`}
          </Box>
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        <HighlightedText
          text={`${member.household?.name || '-'}`}
          query={searchQuery}
        />
        {certification?.property?.name
          ? ` at ${certification.property.name} `
          : ''}
        {member.household?.unit?.name
          ? 'Unit: ' + member.household?.unit?.name || '-'
          : ''}
        {certificationCategory ? ` • ${certificationCategory}` : ''}
      </Typography>
      {hints.length > 0 && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            fontStyle: 'italic',
            display: 'block',
            fontSize: '0.7rem',
          }}
          data-testid="top-bar-search-match-hint"
        >
          Matched: {hints.map((h) => h.label).join(', ')}
        </Typography>
      )}
    </HouseholdMemberBoxStyle>
  );
};

const SearchStyle = styled(OutlinedInput)(() => ({
  width: '100%',
  maxHeight: 56,
}));

export const StatusNotesBubbleFormV2 = ({ onClose }) => {
  const dispatch = useDispatch();
  const {
    results: searchResults,
    count: searchCount,
    capped: searchCapped,
    loading,
  } = useSelector((state) => state.householdSearch);
  const { user } = useAuth();

  const properties = useMemo(() => {
    if (!user.properties?.length) {
      return [{ id: 0, name: 'All Properties' }];
    }
    const userProperties = user.properties.map((property) => ({
      id: property.id,
      name: property.name,
    }));
    return [...userProperties, { id: 0, name: 'All Properties' }];
  }, [user.properties]);

  const [search, setSearch] = useState('');
  const [currentSearch, setCurrentSearch] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [currentCertification, setCurrentCertification] = useState(null);
  const [refreshNotes, setRefreshNotes] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [selectingProperty, setSelectingProperty] = useState({
    id: 0,
    name: 'All Properties',
  });
  const [includeTerminalStatusCerts, setIncludeTerminalStatusCerts] =
    useState(true);

  const fetchSearch = useCallback(() => {
    dispatch(
      fetchHouseholdSearchResults({
        search: search || undefined,
        propertyId: selectingProperty?.id ?? 0,
        includeTerminalStatusCerts,
      })
    );
  }, [dispatch, search, selectingProperty?.id, includeTerminalStatusCerts]);

  useEffect(() => {
    if (search !== null) {
      if (search !== '') {
        const timeOutId = setTimeout(() => {
          setCurrentSearch(search);
          fetchSearch();
        }, 500);
        return () => clearTimeout(timeOutId);
      }
      setCurrentSearch(null);
      dispatch(clearHouseholdSearchResults());
    }
  }, [
    search,
    selectingProperty,
    includeTerminalStatusCerts,
    dispatch,
    fetchSearch,
  ]);

  const handleSuccess = () => {
    setRefreshNotes(true);
    handleCancelEditNote();
  };

  const handleEditNote = (statusNote) => {
    setEditNote(statusNote);
  };

  const handleCancelEditNote = () => {
    setEditNote(null);
  };

  const handleClearSearch = useCallback(() => {
    setSearch('');
  }, []);

  const handleBackToSearch = useCallback(() => {
    setSelectedMember(null);
    setCurrentCertification(null);
  }, []);

  const handleClick = (member, certification) => {
    setSelectedMember(member);
    if (certification) {
      setCurrentCertification(certification);
    } else {
      setCurrentCertification(null);
    }
  };

  const handleUpdateSearch = (event) => {
    setSearch(event.target.value);
  };

  useEffect(() => {
    if (currentCertification) {
      setRefreshNotes(true);
    }
  }, [currentCertification]);

  useEffect(() => {
    if (selectedMember && !currentCertification) {
      const inactiveCertifications = selectedMember?.household?.certifications
        ?.filter((cert) => !cert.is_active && cert.is_done)
        .sort(
          (a, b) =>
            new Date(b.created).getTime() - new Date(a.created).getTime()
        );
      setCurrentCertification(
        selectedMember.active_certification || inactiveCertifications?.[0]
      );
    }
  }, [selectedMember, currentCertification]);

  useEffect(() => {
    if (selectedMember && currentCertification) {
      const belongsToMember = selectedMember?.household?.certifications?.some(
        (cert) => cert.id === currentCertification.id
      );
      if (!belongsToMember) {
        setCurrentCertification(null);
      }
    }
  }, [selectedMember, currentCertification]);

  const householdMembersList = useMemo(
    () =>
      searchResults
        ? {
            results: searchResults,
            count: searchCount,
            capped: searchCapped,
          }
        : null,
    [searchResults, searchCount, searchCapped]
  );

  const buildMatchingCerts = (member) => {
    return (
      member.household?.certifications?.filter((cert) => {
        const isActive =
          cert.status?.is_terminal !== undefined
            ? cert.status.is_terminal === false
            : cert.is_active && !cert.is_done;
        const isTerminal = cert.status?.is_terminal === true;
        if (includeTerminalStatusCerts && isTerminal) {
          if (selectingProperty?.id && selectingProperty?.id !== 0) {
            return cert.property?.id === selectingProperty.id;
          }
          return true;
        }
        if (!isActive) {
          return false;
        }
        if (selectingProperty?.id && selectingProperty?.id !== 0) {
          return cert.property?.id === selectingProperty.id;
        }
        return true;
      }) || []
    );
  };

  return (
    <>
      <Box>
        <Grid2 container sx={{ p: 2 }}>
          <Grid2
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              flexDirection: 'column',
            }}
            size={12}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <SearchStyle
                autoFocus
                value={search}
                onChange={handleUpdateSearch}
                placeholder="Search household members"
                inputProps={{ 'data-testid': 'top-bar-search-input' }}
                sx={{ flex: 1, minWidth: 0 }}
                startAdornment={
                  <InputAdornment position="start">
                    <Box
                      component={Icon}
                      icon={searchFill}
                      sx={{ color: 'text.disabled' }}
                    />
                  </InputAdornment>
                }
                endAdornment={
                  search.length > 0 ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        aria-label="Clear search"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              />
              <Autocomplete
                limitTags={1}
                disableClearable
                options={properties || []}
                getOptionLabel={(option) => option.name}
                value={selectingProperty}
                sx={{ minWidth: 200, flexShrink: 0 }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, newValue) => {
                  setSelectingProperty(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Property"
                    error={false}
                    helperText=""
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{params.InputProps.endAdornment}</>,
                    }}
                  />
                )}
              />
              <Tooltip title="Include terminal status certs" arrow>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Switch
                    checked={includeTerminalStatusCerts}
                    onChange={(e) =>
                      setIncludeTerminalStatusCerts(e.target.checked)
                    }
                    size="small"
                    color="primary"
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', mt: 0.25 }}
                  >
                    Include terminal
                  </Typography>
                </Box>
              </Tooltip>
              <Box
                sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
              >
                {selectedMember && (
                  <IconButton
                    onClick={handleBackToSearch}
                    size="large"
                    aria-label="Back to search results"
                  >
                    <ArrowBack fontSize="small" />
                  </IconButton>
                )}
                <IconButton onClick={onClose} size="large">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Grid2>
          <Grid2
            sx={{
              mt: 1.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 1,
            }}
            size={12}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              You are searching within the properties you have access to. Search
              by name, email, phone, household name, head of household,
              certification name, or log number.
            </Typography>
            {selectedMember && (
              <Button
                onClick={handleBackToSearch}
                startIcon={<ArrowBack fontSize="small" />}
                size="small"
                aria-label="Return to results"
                sx={{ textTransform: 'none' }}
              >
                Return to results
              </Button>
            )}
          </Grid2>
          {selectedMember && (
            <SelectedMemberDetails
              selectedMember={selectedMember}
              currentCertification={currentCertification}
            />
          )}
          <Grid2 size={12}>
            <Divider sx={{ my: 2 }} />
          </Grid2>
          {selectedMember ? (
            <Grid2 sx={{ height: 'calc(100vh - 440px)' }} size={12}>
              <Scrollbar style={{ paddingRight: '16px', paddingTop: '6px' }}>
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', mb: 2 }}
                >
                  Status Notes
                </Typography>
                {currentCertification ? (
                  currentCertification.is_active &&
                  !currentCertification.is_done ? (
                    <>
                      <StatusNotesForm
                        certification={currentCertification}
                        onAddSuccess={handleSuccess}
                        fromBubble={true}
                      />
                      <Divider sx={{ my: 2 }} />
                      <StatusNotesRecentList
                        certificationId={currentCertification.id}
                        refreshNotes={refreshNotes}
                        setRefreshNotes={setRefreshNotes}
                        onEditNote={handleEditNote}
                      />
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      <Typography
                        variant="body1"
                        align="center"
                        sx={{ my: 2, color: 'text.disabled' }}
                      >
                        You cannot add a Status Note because current
                        certification is done or inactive.
                      </Typography>
                    </Box>
                  )
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <Typography
                      variant="body1"
                      align="center"
                      sx={{ my: 2, color: 'text.disabled' }}
                    >
                      You cannot add a Status Note because no certification was
                      found.
                    </Typography>
                  </Box>
                )}
              </Scrollbar>
            </Grid2>
          ) : (
            <Grid2
              sx={{
                height: selectedMember
                  ? 'calc(100vh - 440px)'
                  : 'calc(100vh - 310px)',
              }}
              size={12}
            >
              <Scrollbar style={{ paddingRight: '16px' }}>
                {loading ? (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : currentSearch ? (
                  householdMembersList?.results &&
                  householdMembersList?.count > 0 ? (
                    <Box>
                      {householdMembersList.capped && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: 'block',
                            mb: 1,
                          }}
                        >
                          Showing first 2,000 results. Narrow your search for
                          more specific results.
                        </Typography>
                      )}
                      {(() => {
                        const results = [];
                        const seenKeys = new Set();
                        householdMembersList.results.forEach((member) => {
                          const matchingCerts = buildMatchingCerts(member);
                          matchingCerts.forEach((cert) => {
                            const key = `${member.id}-${cert.id}`;
                            if (!seenKeys.has(key)) {
                              seenKeys.add(key);
                              results.push({
                                member,
                                certification: cert,
                                key,
                              });
                            }
                          });
                        });
                        return results.map(({ member, certification, key }) => (
                          <HouseholdMemberLabel
                            key={key}
                            member={member}
                            handleClick={handleClick}
                            certificationCategory={certification.category?.name}
                            certification={certification}
                            searchQuery={currentSearch}
                          />
                        ));
                      })()}
                    </Box>
                  ) : (
                    <Box sx={{ py: 2, width: '100%' }}>
                      <SearchNotFound searchQuery={currentSearch} />
                    </Box>
                  )
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 2,
                      color: 'text.secondary',
                      textAlign: 'center',
                    }}
                  >
                    Type in the search box to see household members.
                  </Typography>
                )}
              </Scrollbar>
            </Grid2>
          )}
        </Grid2>
      </Box>
      <Dialog open={!!editNote} onClose={handleCancelEditNote} maxWidth="sm">
        <DialogTitle>Edit Note</DialogTitle>
        <DialogContent>
          {editNote && (
            <StatusNotesForm
              isEdit={true}
              statusNote={editNote}
              certification={currentCertification}
              onAddSuccess={handleSuccess}
              onCancel={handleCancelEditNote}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
